// @ts-nocheck
// Tela 1 — Hub de Clubes/Grupos.
import React, { useState, useMemo } from 'react';
import { Icon } from '../icons';
import { BookCover, Flame } from '../common';
import { useTerm, Avatar, LivePulse, UsersIcon } from './components';
import { Club, PendingInvite, ExploreClub } from './data';

function cdLabel(iso: string): string {
  const diff = Date.parse(iso) - Date.now();
  if (!Number.isFinite(diff)) return 'a definir';
  if (diff <= 60000) return 'começa hoje';
  const d = Math.floor(diff / 86400000);
  if (d >= 1) return `${d} ${d === 1 ? 'dia' : 'dias'}`;
  return `${Math.floor(diff / 3600000)} h`;
}

function ProposalMiniCard({ p, onClick }: any) {
  const isLaunch = p.status === 'lancamento';
  const start = p.startAt || p.proposedStartAt;
  return (
    <button onClick={onClick} style={{ width: '100%', textAlign: 'left', position: 'relative', background: 'linear-gradient(160deg, rgba(232,122,59,0.12), rgba(255,255,255,0.02))', border: '1px dashed rgba(232,122,59,0.5)', borderRadius: 22, padding: 16, cursor: 'pointer' }}>
      <div style={{ position: 'absolute', top: 14, right: 14 }}>
        <span className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', background: isLaunch ? 'var(--ember)' : 'var(--paper-3)', color: isLaunch ? 'var(--paper)' : 'var(--ink-2)', padding: '4px 8px', borderRadius: 7, fontWeight: 600 }}>{isLaunch ? 'LANÇAMENTO' : 'PROPOSTA'}</span>
      </div>
      <div className="serif" style={{ fontSize: 21, lineHeight: 1, color: 'var(--ink)', paddingRight: 96 }}>{p.name}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>por {p.owner?.name || '—'} · {p.books} {p.books === 1 ? 'livro' : 'livros'}</div>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.18)', borderRadius: 12, padding: '10px 12px' }}>
        <div>
          <div className="mono" style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>Começa em</div>
          <div className="serif" style={{ fontSize: 18, color: 'var(--ember)', marginTop: 2 }}>{start ? cdLabel(start) : 'a definir'}</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>{isLaunch ? `${p.reserved} reservaram` : `${p.interested} curtiram`}</div>
      </div>
    </button>
  );
}

export const ClubsHubScreen: React.FC<{
  term?: 'clube' | 'grupo';
  // Dados — vêm carregados do App.tsx via api.getClubs()
  clubs?: Club[];
  pendingInvites?: PendingInvite[];
  exploreClubs?: ExploreClub[];
  loading?: boolean;
  // Clube Social: propostas/lançamentos que me convidaram ou que eu propus
  proposals?: any[];
  // Ações
  onOpenClub?: (clubId: string) => void;
  onCreate?: () => void;
  onPropose?: () => void;
  onOpenProposta?: (clubId: string) => void;
  onAcceptInvite?: (token: string, inviteId: string) => void;
  onRejectInvite?: (token: string, inviteId: string) => void;
  // Click em "Explorar todos →" abre a tela full /explorar
  onExploreAll?: () => void;
}> = ({ term = 'clube', clubs = [], pendingInvites = [], exploreClubs = [], proposals = [], loading = false, onOpenClub, onCreate, onPropose, onOpenProposta, onAcceptInvite, onRejectInvite, onExploreAll }) => {
  const T = useTerm(term);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtro local nos clubes que o user já é membro. O search só aparece quando
  // user toca na lupa e tem texto. Match case-insensitive em nome + descrição.
  // Clube Social: clubes ATIVOS só (proposta/lançamento vão na seção de propostas)
  const activeClubs = useMemo(() => clubs.filter((c: any) => !c.status || c.status === 'ativo'), [clubs]);
  const filteredClubs = useMemo(() => {
    if (!searchQuery.trim()) return activeClubs;
    const q = searchQuery.toLowerCase().trim();
    return activeClubs.filter((c: any) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q)
    );
  }, [activeClubs, searchQuery]);

  const empty = !loading && activeClubs.length === 0 && proposals.length === 0;
  const handleOpen = onOpenClub || (() => {});
  const handleCreate = onCreate || (() => {});

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      {/* Header */}
      <div style={{ padding: '58px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            {T.P}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setSearchOpen(v => !v);
                if (searchOpen) setSearchQuery(''); // ao fechar, limpa
              }}
              style={{
                width: 38, height: 38, borderRadius: '50%',
                background: searchOpen ? 'var(--ember-soft)' : 'var(--paper-2)',
                border: '1px solid ' + (searchOpen ? 'var(--ember)' : 'var(--line)'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}>
              <Icon name={searchOpen ? 'close' : 'search'} size={18} color={searchOpen ? 'var(--ember)' : undefined}/>
            </button>
          </div>
        </div>
        <div className="serif" style={{ fontSize: 38, lineHeight: 1.05, marginTop: 18, letterSpacing: '-0.01em' }}>
          Meus <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{T.p}</span>.
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8, maxWidth: 320 }}>
          Leitura compartilhada, no mesmo ritmo. Sem barulho.
        </div>

        {/* Campo de busca expansível — aparece quando user toca na lupa.
           Filtra os clubes que ele já participa por nome ou descrição. */}
        {searchOpen && (
          <div style={{ marginTop: 16, position: 'relative' }}>
            <input
              autoFocus
              placeholder={`Buscar entre seus ${T.p}…`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '12px 40px 12px 16px',
                borderRadius: 12, border: '1px solid var(--line)',
                background: 'var(--paper-2)', color: 'var(--ink)',
                fontSize: 14, outline: 'none', fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute', right: 10, top: '50%',
                  transform: 'translateY(-50%)',
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'var(--paper)', border: '1px solid var(--line)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Icon name="close" size={11}/>
              </button>
            )}
            {searchQuery && (
              <div className="mono" style={{
                fontSize: 10, color: 'var(--ink-3)', marginTop: 6,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>
                {filteredClubs.length === 0
                  ? 'Nenhum resultado'
                  : `${filteredClubs.length} ${filteredClubs.length === 1 ? 'resultado' : 'resultados'}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Entradas: Criar (imediato) vs Propor (com data + convite) */}
      <div style={{ padding: '22px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
        <button onClick={handleCreate} style={{ textAlign: 'left', background: 'var(--ember-soft)', border: '1px solid var(--ember)', borderRadius: 18, padding: '15px 14px', cursor: 'pointer' }}>
          <div style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--ember)', display: 'grid', placeItems: 'center', marginBottom: 11 }}>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1v13M1 7.5h13" stroke="var(--paper)" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </div>
          <div className="serif" style={{ fontSize: 20, lineHeight: 1, color: 'var(--ink)' }}>Criar um {T.s}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ember-2)', marginTop: 5 }}>Começa agora mesmo.</div>
        </button>
        <button onClick={() => onPropose && onPropose()} style={{ textAlign: 'left', background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 18, padding: '15px 14px', cursor: 'pointer' }}>
          <div style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--paper-3)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', marginBottom: 11 }}>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="2.2" stroke="var(--ink-2)" strokeWidth="1.3"/><path d="M2 6.5h12M5 1.5v3M11 1.5v3" stroke="var(--ink-2)" strokeWidth="1.3" strokeLinecap="round"/></svg>
          </div>
          <div className="serif" style={{ fontSize: 20, lineHeight: 1, color: 'var(--ink)' }}>Propor um {T.s}</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 5 }}>Defina a data e convide.</div>
        </button>
      </div>

      {/* Propostas & lançamentos — convites recebidos ou que eu propus */}
      {proposals.length > 0 && (
        <div style={{ padding: '26px 20px 0' }}>
          <div className="mono" style={{ fontSize: 10.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 12 }}>Propostas & lançamentos</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {proposals.map((p: any) => <ProposalMiniCard key={p.id} p={p} onClick={() => onOpenProposta && onOpenProposta(p.id)} />)}
          </div>
        </div>
      )}

      {/* Estado vazio compacto — só o hint, sem dominar a tela. Explorar continua visível embaixo. */}
      {empty && (
        <div style={{ padding: '24px 20px 0', textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'var(--paper-2)', margin: '0 auto',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px dashed var(--line-strong)',
          }}>
            <UsersIcon size={26} color="var(--ink-4)" stroke={1.4}/>
          </div>
          <div className="serif" style={{ fontSize: 18, marginTop: 12, lineHeight: 1.25, fontStyle: 'italic' }}>
            Sem {T.p} ainda.
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6, maxWidth: 300, margin: '6px auto 0', lineHeight: 1.45 }}>
            Crie o seu, peça um convite, ou explore os {T.p} abertos da comunidade abaixo.
          </div>
        </div>
      )}

      {/* Lista "No seu ritmo" — só quando há clubes do user */}
      {!empty && (
        <>
          <div className="section-head">
            <div className="serif title">{searchQuery ? 'Resultados' : 'No seu ritmo'}</div>
            <span className="mono label">{filteredClubs.length}</span>
          </div>

          <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filteredClubs.length === 0 && searchQuery ? (
              <div style={{
                padding: 30, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13,
                background: 'var(--paper-2)', borderRadius: 14, border: '1px dashed var(--line)',
              }}>
                Nenhum {T.s} encontrado com "{searchQuery}".
              </div>
            ) : (
              [...filteredClubs]
                // Ordena: clubes com novidade primeiro (unreadCount desc), depois mais ativos recentemente
                .sort((a: any, b: any) => {
                  const ua = a.unreadCount || 0;
                  const ub = b.unreadCount || 0;
                  if (ua !== ub) return ub - ua;
                  const la = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
                  const lb = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
                  return lb - la;
                })
                .map(c => <MyClubCard key={c.id} club={c} term={T.s} onClick={() => handleOpen(c.id)}/>)
            )}
          </div>
        </>
      )}

      {/* Convites — só quando existem (independe de ter clubes ou não) */}
      {pendingInvites.length > 0 && (
        <>
          <div className="section-head">
            <div>
              <div className="label">Convites pendentes</div>
              <div className="serif title" style={{ marginTop: 4 }}>Te chamaram</div>
            </div>
          </div>

          <div style={{ padding: '0 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendingInvites.map((inv: any) => (
              <InviteCard
                key={inv.id}
                invite={inv}
                term={T.s}
                onAccept={() => onAcceptInvite && onAcceptInvite(inv.token, inv.id)}
                onReject={() => onRejectInvite && onRejectInvite(inv.token, inv.id)}
              />
            ))}
          </div>
        </>
      )}

      {/* Seção Explorar — SEMPRE visível e GRÁTIS (CS-A): qualquer usuário vê a
         vitrine de clubes públicos. O que continua limitado no free é só ENTRAR
         (cap de clubes), não descobrir. Quando exploreClubs está vazio, mostra
         card "Descobrir grupos abertos" como entrada. */}
      <div className="section-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="serif title">Explorar</div>
        </div>
        {exploreClubs.length > 0 && onExploreAll && (
          <button onClick={onExploreAll} className="label" style={{ background: 'none', cursor: 'pointer' }}>
            Ver todos →
          </button>
        )}
      </div>

      {exploreClubs.length > 0 ? (
        <>
          <div style={{ padding: '0 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {exploreClubs.slice(0, 4).map(c => (
              <ExploreCard key={c.id} club={c} onClick={() => onOpenClub?.(c.id)}/>
            ))}
          </div>
          {/* CTA grande "Explorar todos os grupos públicos" */}
          <div style={{ padding: '14px 20px 0' }}>
            <button onClick={onExploreAll} style={{
              width: '100%', padding: '14px 16px', borderRadius: 14,
              background: 'transparent', border: '1px dashed var(--line-strong)',
              color: 'var(--ink-2)', fontSize: 13, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              🔍 Explorar todos os {term === 'grupo' ? 'grupos' : 'clubes'} públicos →
            </button>
          </div>
        </>
      ) : (
        !loading && (
          <div style={{ padding: '0 20px' }}>
            <button onClick={onExploreAll} style={{
              width: '100%', padding: '20px 16px', borderRadius: 16,
              background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))',
              border: '1px solid var(--ember)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              <div style={{ fontSize: 22 }}>🔍</div>
              <div className="serif" style={{ fontSize: 17, lineHeight: 1.1, color: 'var(--ink)' }}>
                Descobrir <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{term === 'grupo' ? 'grupos' : 'clubes'} abertos</span>
              </div>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.06em', color: 'var(--ink-3)' }}>
                leitura compartilhada da comunidade
              </div>
            </button>
          </div>
        )
      )}
    </div>
  );
};

const MyClubCard: React.FC<{ club: Club; term: string; onClick: () => void }> = ({ club, term, onClick }) => {
  const isGroupRead = club.mode === 'group-read';
  const unread = (club as any).unreadCount || 0;
  return (
    <div className="card" onClick={onClick} style={{
      padding: 0, overflow: 'hidden', position: 'relative', cursor: 'pointer',
      // Borda ember sutil quando há novidade — chama atenção sem ser agressivo
      border: unread > 0 ? '1px solid color-mix(in oklab, var(--ember) 40%, var(--line))' : undefined,
    }}>
      {/* Dot ember discreto no canto superior direito quando há novidade */}
      {unread > 0 && (
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 2,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: 'var(--ember)',
            boxShadow: '0 0 0 3px color-mix(in oklab, var(--ember) 25%, transparent)',
          }}/>
          <span className="mono" style={{
            fontSize: 9, letterSpacing: '0.06em',
            color: 'var(--ember)', fontWeight: 600,
          }}>{unread > 9 ? '9+' : unread} {unread === 1 ? 'novo' : 'novos'}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 14, padding: 14 }}>
        <div style={{ flexShrink: 0, position: 'relative' }}>
          {(() => {
            // F6: capa em Avulso tambem — usa current da mySeries ou myBook como fallback.
            if (isGroupRead && club.book) {
              return <BookCover book={{ title: club.book.title, author: club.book.author, coverHue: club.book.coverHue, coverStyle: club.book.coverStyle, coverUrl: club.book.coverUrl } as any} width={68} height={100}/>;
            }
            if (!isGroupRead) {
              const ms: any = (club as any).mySeries;
              const currentMs = ms && Array.isArray(ms.books) ? ms.books.find((b: any) => b.state === 'current') : null;
              if (currentMs) {
                return <BookCover book={{ title: currentMs.title, author: currentMs.author, coverHue: currentMs.coverHue, coverStyle: currentMs.coverStyle, coverUrl: currentMs.coverUrl } as any} width={68} height={100}/>;
              }
              const mb: any = (club as any).myBook;
              if (mb) {
                return <BookCover book={{ title: mb.title, author: mb.author, coverHue: mb.coverHue, coverStyle: mb.coverStyle, coverUrl: mb.coverUrl } as any} width={68} height={100}/>;
              }
            }
            return null;
          })() || (
            <div style={{
              width: 68, height: 100, borderRadius: 6,
              background: `linear-gradient(135deg, oklch(0.78 0.09 ${club.cover?.hue || 22}), oklch(0.45 0.10 ${club.cover?.hue || 22}))`,
              boxShadow: '0 12px 22px -10px rgba(0,0,0,0.35), inset 2px 0 0 rgba(0,0,0,0.12)',
              position: 'relative', overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <UsersIcon size={28} color="rgba(255,255,255,0.85)" stroke={1.4}/>
              <div style={{
                position: 'absolute', bottom: 12, left: 8, right: 8,
                display: 'flex', gap: 2, justifyContent: 'center',
              }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{
                    width: 4, height: 14 + i*2,
                    background: 'rgba(255,255,255,0.7)', borderRadius: 1,
                  }}/>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="serif" style={{
                fontSize: 19, lineHeight: 1.1, color: 'var(--ink)',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              }}>
                {club.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <Avatar letter={club.admin?.avatar || '?'} size={18} hue={club.cover?.hue || 22}/>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{club.admin?.name || 'Líder'}</div>
              </div>
            </div>
            {club.admin?.verified && (
              <div style={{ flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--ember)">
                  <path d="M12 2l2.4 1.8 3 .2.6 3 2 2.2-1.4 2.7.4 3-2.7 1.4-1.4 2.7-3-.4-2.7 1.4-2.2-2-3-.6-.2-3L2 12l1.8-2.4 .2-3 3-.6 2.2-2L12 2z"/>
                  <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>

          {/* A lista não calcula quem está lendo (vinha sempre 0): só aparece
              quando houver número de verdade. */}
          {club.readingNow > 0 && (
            <div style={{ marginTop: 10 }}>
              <LivePulse count={club.readingNow}/>
            </div>
          )}

          {isGroupRead && club.book && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--ink-3)' }}>
                  você: <strong style={{ color: 'var(--ink)', fontWeight: 600 }}>{club.book.yourPct}%</strong>
                </span>
                <span className="mono" style={{ fontSize: 10, letterSpacing: '0.08em', color: 'var(--ink-4)' }}>
                  {term}: {club.book.collectivePct}%
                </span>
              </div>
              <div style={{ position: 'relative', height: 3, background: 'var(--paper-3)', borderRadius: 2, overflow: 'visible' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${club.book.yourPct}%`,
                  background: 'var(--ember)', borderRadius: 2,
                }}/>
                <div style={{
                  position: 'absolute', top: -2, bottom: -2,
                  left: `${club.book.collectivePct}%`,
                  width: 1.5, background: 'var(--ink-2)',
                }}/>
              </div>
            </div>
          )}

          {!isGroupRead && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Flame count={club.streak} size={16}/>
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-2)' }}>
                <strong style={{ color: 'var(--ember)', fontWeight: 600 }}>{club.streak}d</strong> de chama coletiva
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InviteCard: React.FC<{ invite: PendingInvite; term: string; onAccept?: () => void; onReject?: () => void }> = ({ invite, onAccept, onReject }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: 12,
    background: 'var(--paper-2)',
    border: '1px solid var(--line)',
    borderRadius: 18,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 12,
      background: `linear-gradient(135deg, oklch(0.7 0.09 ${invite.hue}), oklch(0.45 0.10 ${invite.hue}))`,
      flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <UsersIcon size={20} color="rgba(255,255,255,0.85)"/>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="serif" style={{
        fontSize: 16, lineHeight: 1.15,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {invite.clubName}
      </div>
      <div style={{
        fontSize: 11, color: 'var(--ink-3)', marginTop: 2,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {invite.invitedBy} te convidou
      </div>
    </div>
    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
      <button onClick={onReject} style={{
        width: 36, height: 36, borderRadius: '50%',
        background: 'var(--paper)', border: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name="close" size={14} color="var(--ink-3)"/>
      </button>
      <button onClick={onAccept} style={{
        padding: '0 14px', height: 36, borderRadius: 18,
        background: 'var(--ember)', color: 'var(--paper)',
        fontSize: 12, fontWeight: 500,
      }}>
        Aceitar
      </button>
    </div>
  </div>
);

const ExploreCard: React.FC<{ club: ExploreClub; onClick?: () => void }> = ({ club, onClick }) => (
  <div onClick={onClick} style={{
    borderRadius: 18, overflow: 'hidden',
    border: '1px solid var(--line)',
    background: 'var(--paper-2)',
    cursor: onClick ? 'pointer' : 'default',
  }}>
    <div style={{
      height: 96, position: 'relative',
      background: `linear-gradient(135deg, oklch(0.78 0.09 ${club.hue}), oklch(0.45 0.10 ${club.hue}))`,
    }}>
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.18,
        backgroundImage: `repeating-linear-gradient(45deg, oklch(0.55 0.08 ${club.hue})55 0 1px, transparent 1px 6px)`,
      }}/>
      <div style={{
        position: 'absolute', top: 8, right: 8,
        padding: '3px 7px', borderRadius: 6,
        background: 'rgba(255,255,255,0.85)',
        fontFamily: 'Geist Mono, monospace',
        fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--ink-2)',
      }}>{club.tag}</div>
    </div>
    <div style={{ padding: 12 }}>
      <div className="serif" style={{
        fontSize: 15, lineHeight: 1.15, color: 'var(--ink)',
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        minHeight: 34,
      }}>
        {club.name}
      </div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', marginTop: 6 }}>
        {(club.members || 0).toLocaleString('pt-BR')} {(club.members || 0) === 1 ? 'membro' : 'membros'}
      </div>
    </div>
  </div>
);
